import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/ui/combobox'

interface FacadeFinishSectionProps {
  // General finish from project defaults
  generalFinish?: {
    material: string
    color: string
    edgeColor?: string
    edgeColorSameAsDoor: boolean
  }
  
  // Current values
  useGeneral: boolean
  customMaterial?: string
  customColor?: string
  customEdgeColor?: string
  customEdgeColorSameAsDoor?: boolean
  
  // Callbacks
  onUseGeneralChange: (useGeneral: boolean) => void
  onCustomMaterialChange: (material: string) => void
  onCustomColorChange: (color: string) => void
  onCustomEdgeColorChange: (color: string) => void
  onCustomEdgeColorSameChange: (same: boolean) => void
  
  // Options for combobox
  materialOptions?: string[]
  
  // Label for the section (e.g., "portes" or "tiroirs")
  label?: string

  // Dialog control (headless mode)
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FacadeFinishSection({
  generalFinish: _generalFinish,
  useGeneral,
  customMaterial = '',
  customColor = '',
  customEdgeColor = '',
  customEdgeColorSameAsDoor = true,
  onUseGeneralChange,
  onCustomMaterialChange,
  onCustomColorChange,
  onCustomEdgeColorChange,
  onCustomEdgeColorSameChange,
  materialOptions = ['Stratifié', 'Mélamine', 'MDF', 'Placage Bois', 'Semi-massif', 'Massif', 'Autre'],
  label = 'façade',
  open,
  onOpenChange,
}: FacadeFinishSectionProps) {
  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Finition façade ({label})</DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Mode</Label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={useGeneral}
                    onChange={() => onUseGeneralChange(true)}
                    className="w-4 h-4"
                  />
                  <span>Finition de base</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!useGeneral}
                    onChange={() => onUseGeneralChange(false)}
                    className="w-4 h-4"
                  />
                  <span>Spécifique ({label})</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Choisissez <span className="font-medium">Finition de base</span> pour utiliser la finition façade du projet,
                ou <span className="font-medium">Spécifique</span> pour définir une façade propre à ces {label}.
              </p>
            </div>

            {!useGeneral && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Définissez ici la finition façade spécifique pour ces {label}. Les champs marqués d&apos;une * sont obligatoires.
                </p>

                {/* Material */}
                <div className="space-y-2">
                  <Label>Matériau *</Label>
                  <Combobox
                    options={materialOptions.map((material) => ({
                      value: material,
                      label: material,
                    }))}
                    value={customMaterial}
                    onChange={onCustomMaterialChange}
                    placeholder="Rechercher ou saisir un matériau..."
                    searchPlaceholder="Rechercher..."
                    emptyText="Aucun matériau trouvé."
                    allowCustomValue={true}
                  />
                </div>

                {/* Color */}
                <div className="space-y-2">
                  <Label>Coloris {label} *</Label>
                  <Input
                    value={customColor}
                    onChange={(e) => onCustomColorChange(e.target.value)}
                    placeholder="Ex: Chêne Halifax, Blanc mat..."
                  />
                </div>

                {/* Edge Color */}
                <div className="space-y-2">
                  <Label>Coloris du chant (rebord)</Label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={customEdgeColorSameAsDoor}
                        onChange={() => onCustomEdgeColorSameChange(true)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm">Idem façades</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!customEdgeColorSameAsDoor}
                        onChange={() => onCustomEdgeColorSameChange(false)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm">Différent :</span>
                      {!customEdgeColorSameAsDoor && (
                        <Input
                          value={customEdgeColor}
                          onChange={(e) => onCustomEdgeColorChange(e.target.value)}
                          placeholder="Coloris"
                          className="flex-1"
                        />
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={!useGeneral && (!customMaterial || !customColor)}
            >
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  )
}
